/**
 * GenericViewSwitcher - component to switch between generic views (grid, list, timeline, table)
 * Excludes kanban and calendar which are task-specific
 */

import type { GenericViewType } from '../types';

interface ViewSwitcherProps {
  currentView: GenericViewType;
  onViewChange: (view: GenericViewType) => void;
  availableViews?: GenericViewType[];
}

const allViews: { id: GenericViewType; label: string; icon: string }[] = [
  {
    id: 'grid',
    label: 'Grid',
    icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z',
  },
  {
    id: 'list',
    label: 'List',
    icon: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  },
  {
    id: 'timeline',
    label: 'Timeline',
    icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
  },
  {
    id: 'table',
    label: 'Table',
    icon: 'M3 10h18M3 14h18M3 18h18M3 6h18',
  },
  {
    id: 'files',
    label: 'Files',
    icon: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
  },
];

export function GenericViewSwitcher({
  currentView,
  onViewChange,
  availableViews,
}: ViewSwitcherProps) {
  const views = availableViews
    ? allViews.filter((v) => availableViews.includes(v.id))
    : allViews;

  return (
    <div className="flex items-center bg-neutral-100 dark:bg-neutral-800 rounded-lg p-1">
      {views.map((view) => (
        <button
          key={view.id}
          onClick={() => onViewChange(view.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            currentView === view.id
              ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm'
              : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
          }`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={view.icon}
            />
          </svg>
          <span className="hidden sm:inline">{view.label}</span>
        </button>
      ))}
    </div>
  );
}
